import { DecoratedHorizontalFlatList } from '@euler/app/components/list/FlatListWithMask';
import { Typography } from '@euler/app/components/typography';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Colors, StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ConstructionJob, TaskDetail } from '@euler/model/task-detail';
import { usePersistFn } from '@euler/utils/hooks';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  ListRenderItem,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export type ConstructionBottomTabType = 'overview' | 'job';

export type ConstructionBottomTabInfo =
  | {
      type: 'overview';
    }
  | {
      type: 'job';
      job: ConstructionJob;
    };

type LayoutInfo = {
  totalWidth: number;
  targetWidth: number;
  tabWidths: Map<number, number | null>;
  scale: number;
  useFlex: boolean;
};

export const WiredConstructionBottomTabs = memo(
  ({
    detail,
    style,
    activeTab,
    onTabPress,
  }: {
    style?: StyleProp<ViewStyle>;
    detail: TaskDetail;
    activeTab: ConstructionBottomTabInfo;
    onTabPress?: (tab: ConstructionBottomTabInfo) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const { width } = useWindowDimensions();
    const [paddingLeft, paddingRight] = useScreenContentPadding();

    const translateY = useSharedValue(rpx(148));

    const animatedStyle = useAnimatedStyle(() => {
      return { transform: [{ translateY: translateY.value }] };
    });

    const totalWidth = width - paddingLeft - paddingRight;
    const numberOfJobs = detail.construction.jobs.length;
    const tabCount = numberOfJobs + 1;
    const jobIdList = useMemo(
      () => detail.construction.jobs.map(x => x.id),
      [detail.construction.jobs],
    );
    const tabLayouts = useRef(new Map<number, number>());
    const [layoutInfo, setLayoutInfo] = useState<LayoutInfo>();

    const onOverviewTabPress = useCallback(() => {
      onTabPress?.({ type: 'overview' });
    }, [onTabPress]);

    const onJobTabPress = useCallback(
      (job: ConstructionJob) => {
        onTabPress?.({ type: 'job', job });
      },
      [onTabPress],
    );

    const updateLayoutIfNecessary = usePersistFn(() => {
      if (layoutInfo != null) return;

      const widthList = [...tabLayouts.current.values()];

      if (widthList.length < tabCount || widthList.some(x => x <= 0)) return;

      const allTabsWidth = widthList.reduce((s, x) => s + x);

      const tabWidths = new Map<number, number | null>();
      let useFlex = false;
      let scale = 1;
      if (allTabsWidth <= totalWidth || allTabsWidth - totalWidth < rpx(5)) {
        scale = totalWidth / allTabsWidth;
        // make the tab flexes automatically
        for (const [key, value] of tabLayouts.current) {
          tabWidths.set(key, value * scale);
        }
        useFlex = true;
      } else {
        // otherwise make them scroll
        tabWidths.set(0, null);
        for (const jobId of jobIdList) {
          tabWidths.set(jobId, null);
        }
      }
      setLayoutInfo({
        totalWidth,
        targetWidth: allTabsWidth,
        tabWidths,
        useFlex,
        scale,
      });
    });

    const onTabLayout = usePersistFn(
      (layout: LayoutRectangle, job?: ConstructionJob) => {
        if (layoutInfo != null) return;
        const map = new Map(tabLayouts.current);
        map.set(job?.id ?? 0, layout.width);
        tabLayouts.current = map;
        updateLayoutIfNecessary();
      },
    );

    const renderItem: ListRenderItem<ConstructionJob> = usePersistFn(
      ({ item }) => {
        return (
          <JobTabCell
            job={item}
            width={layoutInfo?.tabWidths.get(item.id)}
            active={activeTab.type === 'job' && activeTab.job.id === item.id}
            onPress={onJobTabPress}
            onLayout={onTabLayout}
          />
        );
      },
    );

    useEffect(() => {
      if (layoutInfo != null) {
        translateY.value = withTiming(0, {
          duration: 250,
          easing: Easing.inOut(Easing.linear),
        });
      }
    }, [tabCount, jobIdList, layoutInfo, totalWidth, translateY]);

    if (numberOfJobs === 0) {
      return null;
    }

    return (
      <Animated.View
        css={`
          flex-direction: row;
          align-items: stretch;
          justify-content: space-between;
          background-color: ${theme.task.bottomBar.bg};
          border-top-left-radius: ${rpx(40)}px;
          border-top-right-radius: ${rpx(40)}px;
        `}
        style={[style, animatedStyle]}
      >
        <TabCell
          text="综合"
          debugLabel="default tab"
          active={activeTab.type === 'overview'}
          onPress={onOverviewTabPress}
          onLayout={onTabLayout}
          width={layoutInfo?.tabWidths.get(0)}
        />
        <DecoratedHorizontalFlatList
          horizontal
          data={detail.construction.jobs}
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          keyExtractor={x => String(x.id)}
          renderItem={renderItem}
          noMask={layoutInfo?.useFlex}
          contentContainerStyle={{
            flexGrow: 1,
            alignItems: 'stretch',
            justifyContent: layoutInfo?.useFlex
              ? 'space-between'
              : 'flex-start',
          }}
          css={`
            flex: 1;
          `}
        />
      </Animated.View>
    );
  },
);

const JobTabCell = memo(
  ({
    job,
    active,
    width,
    onPress,
    onLayout,
  }: {
    job: ConstructionJob;
    active?: boolean;
    width?: number | null;
    onPress?: (job: ConstructionJob) => void;
    onLayout?: (layout: LayoutRectangle, job: ConstructionJob) => void;
  }) => {
    const handlePress = useCallback(() => {
      onPress?.(job);
    }, [job, onPress]);

    const handleLayout = useCallback(
      (layout: LayoutRectangle) => {
        onLayout?.(layout, job);
      },
      [job, onLayout],
    );

    return (
      <TabCell
        debugLabel={`job (#${job.id})`}
        text={job.name}
        width={width}
        onPress={handlePress}
        active={active}
        onLayout={handleLayout}
      />
    );
  },
);

const kRedDotSize = 5;
const kRedDotPadding = 0;

const TabCell = memo(
  ({
    width,
    text,
    active,
    style,
    redDot,
    redDotStyle,
    children,
    onPress,
    onLayout,
  }: {
    debugLabel: string;
    width?: number | null;
    text: string;
    active?: boolean;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    redDot?: boolean;
    redDotStyle?: StyleProp<ViewStyle>;
    onPress?: () => void;
    onLayout?: (layout: LayoutRectangle) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [labelLayout, setLabelLayout] = useState<LayoutRectangle>();

    const onLabelLayout = useCallback((e: LayoutChangeEvent) => {
      setLabelLayout(e.nativeEvent.layout);
    }, []);

    const onCellLayout = useCallback(
      (e: LayoutChangeEvent) => {
        onLayout?.(e.nativeEvent.layout);
      },
      [onLayout],
    );

    return (
      <TouchableOpacity
        css={`
          padding: 0 ${rpx(60)}px;
          justify-content: center;
          align-items: center;
        `}
        style={[style, { width: width ?? undefined }]}
        onPress={onPress}
        onLayout={onCellLayout}
      >
        <Typography.Label
          medium
          color={active ? Colors.White : Colors.White3}
          size={rpx(32)}
          onLayout={onLabelLayout}
          numberOfLines={1}
          css={`
            text-align: center;
            font-family: ${active
              ? FontFamily.NotoSans.Black
              : FontFamily.NotoSans.Regular};
          `}
        >
          {text}
        </Typography.Label>
        {redDot && labelLayout != null ? (
          <View
            css={`
              position: absolute;
              left: ${labelLayout.x + labelLayout.width + rpx(2)}px;
              top: ${labelLayout.y}px;
              width: ${rpx(kRedDotSize)}px;
              height: ${rpx(kRedDotSize)}px;
              border-radius: ${rpx(kRedDotSize) / 2}px;
              background-color: ${Colors.White0_5};
              padding: ${rpx(kRedDotPadding)}px;
            `}
          >
            <View
              css={`
                background-color: ${StatusColors.Danger};
                width: 100%;
                height: 100%;
                border-radius: ${rpx(kRedDotSize - kRedDotPadding * 2) / 2}px;
              `}
              style={redDotStyle}
            />
          </View>
        ) : null}
        {children}
      </TouchableOpacity>
    );
  },
);
